export const en = {
    translation: {
        common: {
            cancel: 'Cancel',
            close: 'Close',
            save: 'Save',
        },
        createOrganization: {
            welcome: {
                title: 'Welcome to {{productName}}',
                description:
                    'This assistant will guide you through the process of creating your organization and setting up your workspace.',
                nameLabel: 'Name of your organization',
                namePlaceholder: 'Enter the name of your organization',
                start: 'Lets get started',
            },
            organization: {
                badge: 'Organization details',
                title: 'Tell us a bit more about {{organizationName}}',
                description: 'We use this to prepare the right setup, terminology, and defaults for your workspace.',
                classificationTypeLabel: 'Type of your organization',
                classificationTypePlaceholder: 'Please select',
                classificationAreaLabel: 'Activity area',
                classificationAreaPlaceholder: 'Please select',
                sportsLabel: 'Sports',
                addressLabel: 'Organization address',
                addressHint:
                    'We need the address to configure country-specific defaults, documents, tax settings, and organization data correctly from the start.',
                continue: 'Continue',
            },
            accounting: {
                title: 'How should accounting work?',
                description:
                    'Choose how you want to set up accounting for your organization. This can not be changed later.',
                continue: 'Continue',
                mode: {
                    options: {
                        none: {
                            title: 'No accounting',
                            description: 'Skip accounting for now and keep the finance setup lightweight.',
                        },
                        export: {
                            title: 'Export only',
                            description: 'Track finance data and export postings to another accounting system.',
                        },
                        full: {
                            title: 'Full accounting',
                            description: 'Use the complete accounting features directly in the product.',
                        },
                    },
                },
                plan: {
                    label: 'Account plan',
                    placeholder: 'Please select',
                    warning:
                        'This account plan cannot be changed afterwards. To use a different one later, you would need to create a new organization.',
                },
                fiscalMonth: {
                    label: 'Fiscal year starts in',
                },
                vatDueMethod: {
                    label: 'Tax due method',
                },
            },
            finish: {
                title: 'You are all set',
                description: 'Congratulations, {{organizationName}} is ready to be created.',
                trial: 'You can now test everything for 14 days in a free trial.',
                memberSize: {
                    label: 'Expected member size',
                    description: 'This will be used to further customize the app for your organization.',
                },
                website: {
                    label: 'Website',
                    description: 'Add your website so we can tailor the setup to your public presence.',
                    error: 'Please enter a valid website URL including https://.',
                    noWebsite: 'I have no website',
                },
                submit: 'Create organization now',
                creating: 'Creating organization...',
                success: {
                    title: 'Congratulations',
                    description:
                        'Your organization is ready. You can open it now and continue with the setup inside the app.',
                    link: 'Open organization now',
                },
                error: 'The organization could not be created right now. Please try again.',
            },
        },
        home: {
            title: 'Start',
            intro: 'Manage your organizations and personal profile within this app.',
            organizations: 'Your organizations',
            description: 'Open an organization in the right app or create a new one.',
            searchPlaceholder: 'Search organizations',
            createAction: 'Create organization',
            loadMore: 'Load more',
            empty: {
                title: 'Create your own organization',
                description:
                    'Create a tailored home for your organization and unlock the full {{productName}} experience.',
            },
            demo: {
                title: 'Explore the live demo',
                description:
                    'Step into a ready-made organization and see how {{productName}} works with real-looking data.',
            },
            searchEmpty: {
                title: 'No organizations found',
                description: 'Adjust your search to find an organization.',
            },
            system: {
                title: 'System',
                description: 'Manage system-wide settings and data.',
                action: 'Open system',
            },
            community: {
                title: 'Community',
                description: 'Ask questions and get help from the community around the product',
                action: 'Open community',
            },
            feedback: {
                title: 'Feedback',
                description: 'Share improvements and new product features with us',
                action: 'Open feedback',
            },
            profileSummary: {
                bioEmpty: 'Add a short bio to tell others a bit about yourself.',
                editAction: 'Edit profile',
                logoutAction: 'Logout',
            },
        },
        notifications: {
            panel: {
                title: 'Notifications',
                loading: 'Loading notifications...',
                empty: 'No notifications found',
                markAllRead: 'Mark all as read',
                markRead: 'Mark read',
                markUnread: 'Mark unread',
                delete: 'Delete',
                showDetails: 'Show details',
                unread: 'Unread',
                all: 'All',
                categoryFilter: 'Category',
                count_one: '{{count, number}} notification',
                count_other: '{{count, number}} notifications',
                countShowing: '{{shown, number}} of {{count, number}} notifications',
                loadMore: 'Load more',
                detailsDialog: {
                    title: 'Notification details',
                },
            },
        },
        product: {
            feedback: {
                ticket: {
                    loading: 'Loading feedback ticket',
                    number: 'Feedback ticket #{{number}}',
                    createdAt: 'Opened {{date}} by {{author}}',
                    status: 'Status',
                    upvoteAction: 'Upvote',
                    removeUpvoteAction: 'Remove upvote',
                    votes_one: '{{count, number}} vote',
                    votes_other: '{{count, number}} votes',
                    metadata: 'Edit status and tags',
                    follow: 'Follow ticket',
                    unfollow: 'Unfollow ticket',
                    edit: 'Edit ticket',
                    delete: 'Delete ticket',
                    comments_one: '{{count, number}} comment',
                    comments_other: '{{count, number}} comments',
                    reply: 'Add comment',
                    deleteDialog: {
                        title: 'Delete feedback ticket?',
                        message: 'Delete "{{title}}" and all comments?',
                        confirm: 'Delete ticket',
                    },
                    empty: {
                        title: 'No comments yet',
                        description: 'Add the first comment to continue the discussion.',
                    },
                    metadataDialog: {
                        title: 'Edit status and tags',
                        description: 'Manage internal metadata for this feedback ticket.',
                        tags: 'Tags',
                        tagsPlaceholder: 'Add tags separated by comma',
                    },
                    commentDialog: {
                        titleCreate: 'Add comment',
                        titleEdit: 'Edit comment',
                        description: 'Add more details to this feedback ticket.',
                        message: 'Comment',
                        create: 'Add comment',
                    },
                    comment: {
                        edit: 'Edit comment',
                        delete: 'Delete comment',
                        deleteDialog: {
                            title: 'Delete comment?',
                            message: 'Delete this feedback comment?',
                            confirm: 'Delete comment',
                        },
                    },
                },
                home: {
                    title: 'Feedback',
                    description: 'Share improvements and new product features with us',
                    create: 'Create ticket',
                    searchPlaceholder: 'Search tickets',
                    ticketCount_one: '{{count, number}} Ticket',
                    ticketCount_other: '{{count, number}} Tickets',
                    ticketMeta: '#{{number}} opened {{createdAt}} by <author /> · updated {{latestActivityAt}}',
                    filters: {
                        status: 'Filter by status',
                        sort: 'Sort tickets',
                        tag: 'Filter by tag',
                    },
                    tagFilter: {
                        all: 'All tags',
                        none: 'No tag',
                    },
                    statusFilter: {
                        all: 'All open',
                        new: 'New',
                        planned: 'Planned',
                        inProgress: 'In progress',
                        done: 'Done',
                        rejected: 'Rejected',
                    },
                    sort: {
                        latestActivity: 'Latest activity',
                        newest: 'Newest',
                        oldest: 'Oldest',
                        mostVotes: 'Most votes',
                        mostComments: 'Most comments',
                    },
                    empty: {
                        title: 'No feedback tickets yet',
                        description: 'Create the first ticket to share product feedback.',
                    },
                    searchEmpty: {
                        title: 'No matching feedback tickets',
                        description: 'Adjust your search to find a ticket.',
                    },
                },
                shared: {
                    status: {
                        new: 'New',
                        planned: 'Planned',
                        inProgress: 'In progress',
                        done: 'Done',
                        rejected: 'Rejected',
                    },
                    ticketDialog: {
                        titleCreate: 'Create feedback ticket',
                        titleEdit: 'Edit feedback ticket',
                        description: 'Describe the product feedback you want to share.',
                        ticketTitle: 'Title',
                        message: 'Message',
                        create: 'Create ticket',
                    },
                },
            },
            community: {
                category: {
                    loading: 'Loading category',
                    createTopic: 'Create topic',
                    searchPlaceholder: 'Search topics',
                    openTopic: 'Open topic',
                    createdAt: 'Created {{date}}',
                    lastReplyAt: 'Last reply {{date}}',
                    topicCounts: '{{posts, number}} replies',
                    locked: 'Locked',
                    readState: {
                        new: 'New',
                        updated: 'Updated',
                    },
                    sort: {
                        label: 'Sort topics',
                        newestTopics: 'Newest topics',
                        oldestTopics: 'Oldest topics',
                        mostReplies: 'Most replies',
                        leastReplies: 'Least replies',
                        newestReply: 'Newest reply',
                        oldestReply: 'Oldest reply',
                    },
                    empty: {
                        title: 'No topics yet',
                        description: 'Start the first discussion in this category.',
                    },
                    searchEmpty: {
                        title: 'No matching topics',
                        description: 'Adjust your search to find a topic.',
                    },
                },
                topic: {
                    loading: 'Loading topic',
                    actions: 'Topic actions',
                    edit: 'Edit topic',
                    delete: 'Delete topic',
                    follow: 'Follow topic',
                    unfollow: 'Unfollow topic',
                    pin: 'Pin topic',
                    unpin: 'Unpin topic',
                    pinned: 'Pinned',
                    lock: 'Lock topic',
                    unlock: 'Unlock topic',
                    locked: 'Locked',
                    move: 'Move topic',
                    replies: 'Replies',
                    reply: 'Reply',
                    postCount: '{{posts, number}} replies',
                    empty: {
                        title: 'No replies yet',
                        description: 'Be the first to reply to this topic.',
                    },
                    deleteDialog: {
                        title: 'Delete topic?',
                        message: 'Delete "{{title}}"?',
                        confirm: 'Delete topic',
                    },
                    post: {
                        new: 'New',
                        edit: 'Edit reply',
                        delete: 'Delete reply',
                        deleteDialog: {
                            title: 'Delete reply?',
                            message: 'Delete this reply?',
                            confirm: 'Delete reply',
                        },
                    },
                    moveDialog: {
                        title: 'Move topic',
                        description: 'Select the category this topic should move to.',
                        category: 'Category',
                        categoryPlaceholder: 'Select category',
                        confirm: 'Move topic',
                    },
                    postDialog: {
                        titleCreate: 'Create reply',
                        titleEdit: 'Edit reply',
                        description: 'Replies are shown below the topic.',
                        message: 'Message',
                        create: 'Post',
                    },
                },
                home: {
                    title: 'Community',
                    description: 'Ask questions and get help from the community around the product',
                    categories: {
                        description: 'Choose a category to browse topics or start a new discussion.',
                        create: 'Create category',
                        edit: 'Edit category',
                        delete: 'Delete category',
                        open: 'Open',
                        counts: '{{topics, number}} topics · {{posts, number}} replies',
                        empty: {
                            title: 'No categories yet',
                            description: 'Create the first topic category for community discussions.',
                        },
                        deleteDialog: {
                            title: 'Delete category?',
                            message: 'Delete "{{title}}" and all contained topics and replies?',
                            confirm: 'Delete category',
                        },
                    },
                    categoryDialog: {
                        titleCreate: 'Create category',
                        titleEdit: 'Edit category',
                        description: 'Categories are topic groups for the global community.',
                        title: 'Title',
                        categoryDescription: 'Description',
                        create: 'Create',
                    },
                },
                shared: {
                    attachments: {
                        title: 'Attachments',
                        add: 'Add attachments',
                        remove: 'Remove attachment',
                        download: 'Download attachment',
                    },
                    topicDialog: {
                        titleCreate: 'Create topic',
                        titleEdit: 'Edit topic',
                        description: 'Topics start a new community discussion.',
                        topicTitle: 'Title',
                        message: 'Message',
                        create: 'Create',
                    },
                },
            },
        },
        invitation: {
            title: 'Accept invitation',
            areUserAlready: 'You are already a user of this organization',
            isOwner: 'You are already an owner user of this organization, so you cannot accept this invitation.',
            roleChangeWarning:
                'If you accept this invitation, your user role will be changed and all your permissions will also be changed.',
            info: 'You are already a user of this organization. Accepting this invitation can give you more permissions.',
            loginWithAnotherUser: 'Log in with another user',
            accept: 'Accept invitation',
            message: 'Hello {{userName}}, you were invited to join "{{organizationName}}".',
            error: {
                title: 'Unable to accept invitation',
                subtitle: 'The invitation link is not valid. Please request a new one.',
            },
        },
        profile: {
            title: 'User settings',
            personal: {
                title: 'Personal information',
                description: 'This information is visible for other users in your organization',
                name: 'Your full name',
                email: 'Your E-Mail address',
                changeEmail: 'Change E-Mail',
                language: 'Your preferred language',
                timezone: 'Your timezone',
                bio: 'A little bit about yourself',
            },
            emailChangeDialog: {
                title: 'Change your E-Mail',
                email: 'New E-Mail address',
                save: 'Save',
            },
            security: {
                title: 'Security',
                description: 'Change your password and configure multi-factor authentication methods',
                securitySettings: 'Open security settings',
            },
            'ui-preferences': {
                title: 'UI Preferences',
                description: 'Setup your UI preferences which will only apply to this browser session.',
                colorScheme: 'Your preferred color scheme',
                colorSchemeValue: {
                    system: 'Use the system defaults',
                    light: 'Use light color scheme',
                    dark: 'Use dark color scheme',
                },
            },
            notifications: {
                title: 'Notifications',
                description: 'Choose how you receive notifications.',
                category: 'Category',
                empty: 'No notification categories available',
                saved: 'Notification settings saved',
                error: 'Unable to save notification settings',
                targets: {
                    connect: 'Community-App',
                    root: 'Community',
                    admin: 'Administration',
                },
                channels: {
                    email: 'Email',
                    push: 'Push',
                },
                aria: {
                    email: 'Email notifications for {{category}}',
                    push: 'Push notifications for {{category}}',
                },
            },
            dangerZone: {
                title: 'Danger zone',
                description: 'Take care with the actions you perform here',
                delete: {
                    title: 'Delete account',
                    description:
                        'You will lose access to your account and all your data will be permanently deleted.\nAttention, this cannot be undone!',
                    action: 'Delete',
                    confirmation: {
                        title: 'Delete account',
                        message:
                            'Are you absolutely sure that you want to delete your account and all associated data? This action can not be undone.',
                    },
                },
            },
        },
    },
}
